'use client';

import {useState} from 'react';
import {useRouter} from 'next/navigation';
import {useMutation} from '@tanstack/react-query';
import {deleteBin3Line, fileEditLine, pencilLine} from 'src/utils/icons';
import Dropdown, {type DropdownMenuItem} from '../Dropdown';
import ConfirmDialog from '../ConfirmDialog';
import EditListModal from '../EditListModal';
import fetcher from 'src/utils/fetcher';

const getMenuItems = (
  openEditModal: () => void,
  removeList: () => void,
  list: HumboList,
  isAdmin: boolean
): DropdownMenuItem[] =>
  [
    {
      icon: pencilLine,
      text: 'Edit List',
      key: 'edit',
      onAction: openEditModal
    },
    isAdmin
      ? {
          icon: fileEditLine,
          text: 'Listicle',
          key: 'listicle',
          href: `/admin/listicles/${list.user?.nameId}/${list.nameId}`
        }
      : null,
    {
      icon: deleteBin3Line,
      text: 'Delete List',
      key: 'delete',
      onAction: removeList
    }
  ].filter(Boolean);

type Props = {
  children: React.ReactNode;
  list: HumboList;
  session: IronSession | null;
};

const ListContextMenu = ({children, list, session}: Props) => {
  const router = useRouter();
  const [editModalOpened, setEditModalOpened] = useState(false);

  const isAdmin = Boolean(session?.isAdmin);

  const {mutateAsync: removeList} = useMutation({
    mutationFn: () => fetcher({url: `/api/lists/${list.user?.nameId}/${list.nameId}`, options: {method: 'DELETE'}}),
    onSuccess: () => router.push(`/${session?.nameId}/lists`)
  });

  return (
    <>
      <ConfirmDialog
        message="You sure? You will not be able to recover the list after it's deleted."
        confirmText="Delete list"
        confirmButtonClassName="bg-(--error-color)"
        onConfirm={removeList}
      >
        {({openModal}) => {
          const menuItems = getMenuItems(() => setEditModalOpened(true), openModal, list, isAdmin);

          return <Dropdown items={menuItems}>{children}</Dropdown>;
        }}
      </ConfirmDialog>

      <EditListModal list={list} isOpen={editModalOpened} onClose={() => setEditModalOpened(false)} />
    </>
  );
};

export default ListContextMenu;
